import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { notifyError, notifySuccess } from '@config/tostifyConfig/tostifyConfig';
import { generatePDFFromTemplate } from '@utils/pdfGenerator';
import CustomDatePicker from '@shared/components/CustomDatePicker';
import { buildStaffMovementReportExecution } from '../services/staffMovementReportService';
import {
    handleStaffMovementAccessError,
    useStaffMovementPermissionCheck,
} from '../hooks/useStaffMovement';
import {    getDefaultPeopleStatus,
    getStaffMovementLanguageOptions,
    getStaffMovementOptions,
    getStaffMovementPeopleStatusOptions,
    isSectionVisible,
    STAFF_MOVEMENT_COMPANY_OPTIONS,
    STAFF_MOVEMENT_SECTIONS,
} from '../constants/staffMovementReports';

function StaffMovementSection({ title, children }) {
    return (
        <div className="hr-information-section">
            {title ? <span className="hr-information-section__title">{title}</span> : null}
            {children}
        </div>
    );
}

function LanguageCompanySection({ t, language, onLanguageChange, companyId, onCompanyChange, languageOptions }) {
    return (
        <StaffMovementSection>
            <div className="flex-column gap-10">
                <div className="flex-column gap-10">
                    <span className="no-wrap">{t('staff.staffMovement.language')}</span>
                    <select
                        className="small-select width-100"
                        value={language}
                        onChange={(e) => onLanguageChange(e.target.value)}
                    >
                        <option value="">{t('common.select')}</option>
                        {languageOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="flex-column gap-10">
                    <span className="no-wrap">{t('staff.staffMovement.company')}</span>
                    <select
                        className="small-select width-100"
                        value={companyId}
                        onChange={(e) => onCompanyChange(e.target.value)}
                    >
                        <option value="">{t('common.select')}</option>
                        {STAFF_MOVEMENT_COMPANY_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </StaffMovementSection>
    );
}

function ForPeopleSection({ t, selectedReport, peopleStatus, onPeopleStatusChange, peopleStatusOptions }) {
    return (
        <StaffMovementSection title={t('staff.staffMovement.forPeople')}>
            <div className="flex-column gap-10">
                {peopleStatusOptions.map((option) => (
                    <label key={option.value} className="flex-row gap-10">
                        <input
                            type="radio"
                            name={`peopleStatus-${selectedReport}`}
                            value={option.value}
                            checked={peopleStatus === option.value}
                            onChange={(e) => onPeopleStatusChange(e.target.value)}
                        />
                        <span className="span">{option.label}</span>
                    </label>
                ))}
            </div>
        </StaffMovementSection>
    );
}

function AdditionalColumnsSection({ t, extendedReport, onExtendedReportChange }) {
    return (
        <StaffMovementSection title={t('staff.staffMovement.additionalColumns')}>
            <label className="flex-row gap-10">
                <input
                    className="form-checkbox"
                    type="checkbox"
                    checked={extendedReport}
                    onChange={(e) => onExtendedReportChange(e.target.checked)}
                />
                <span className="span">{t('staff.staffMovement.extendedReport')}</span>
            </label>
        </StaffMovementSection>
    );
}

export default function StaffMovements() {
    const { t } = useTranslation();
    const [selectedReport, setSelectedReport] = useState('');
    const [forPeriodDate, setForPeriodDate] = useState(() => new Date());
    const [fromDate, setFromDate] = useState(() => new Date());
    const [toDate, setToDate] = useState(() => new Date());
    const [language, setLanguage] = useState('');
    const [companyId, setCompanyId] = useState('');
    const [peopleStatus, setPeopleStatus] = useState('working');
    const [extendedReport, setExtendedReport] = useState(false);
    const [executeLoading, setExecuteLoading] = useState(false);

    useStaffMovementPermissionCheck();

    const staffMovementOptions = getStaffMovementOptions(t);
    const languageOptions = getStaffMovementLanguageOptions(t);
    const peopleStatusOptions = getStaffMovementPeopleStatusOptions(t, selectedReport);
    const show = (section) => isSectionVisible(selectedReport, section);

    const fromToDateFields = (
        <div className="flex-row gap-10 flex-wrap align-center">
            <span className="no-wrap">{t('staff.staffMovement.fromDate')}</span>
            <CustomDatePicker
                name="fromDate"
                value={fromDate}
                onChange={setFromDate}
                isSmall={true}
                placeholderText={t('staff.staffMovement.fromDate')}
            />
            <span className="no-wrap">{t('staff.staffMovement.toDate')}</span>
            <CustomDatePicker
                name="toDate"
                value={toDate}
                onChange={setToDate}
                isSmall={true}
                placeholderText={t('staff.staffMovement.toDate')}
            />
        </div>
    );

    useEffect(() => {
        setLanguage('');
        setCompanyId('');
        setPeopleStatus(getDefaultPeopleStatus(selectedReport));
        setExtendedReport(false);
    }, [selectedReport]);

    const handleExecute = useCallback(async () => {
        try {
            setExecuteLoading(true);
            const formState = {
                selectedReport,
                fromDate,
                toDate,
                forPeriodDate,
                language,
                companyId,
                peopleStatus,
                extendedReport,
            };
            const { reportService, reportConfig } = buildStaffMovementReportExecution(
                selectedReport,
                formState,
            );
            const result = await reportService();
            const reportData = result?.reportData ?? result;
            const config = result?.reportConfig ?? reportConfig;
            await generatePDFFromTemplate(reportData, config);
            notifySuccess('PDF отчет генериран успешно!');
        } catch (error) {
            console.error('Error executing staff movement report:', error);
            if (!handleStaffMovementAccessError(error)) {
                const errorMessage = error?.response?.data?.message || error?.message || t('common.errorMessage');
                notifyError(errorMessage);
            }
        } finally {
            setExecuteLoading(false);
        }
    }, [
        selectedReport,
        fromDate,
        toDate,
        forPeriodDate,
        language,
        companyId,
        peopleStatus,
        extendedReport,
        t,
    ]);
    return (
        <div className="flex-column width-50 gap-10">
            <div className="flex-column gap-10">
                <h1 className="text-center">{t('staff.staffMovement.title')}</h1>
                <select
                    className="small-select width-100"
                    value={selectedReport}
                    onChange={(e) => setSelectedReport(e.target.value)}
                >
                    <option value="">{t('common.select')}</option>
                    {staffMovementOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                <div className="checkbox-wrapper-start title-checkbox justify-content-between">
                    <div className="flex-row gap-10">
                        <input
                            className="form-checkbox"
                            type="checkbox"
                            name="saveReport"
                            id="staffMovementSaveReport"
                        />
                        <span className="span-bold">{t('staff.staffMovement.saveReport')}</span>
                    </div>
                    <button
                        type="button"
                        className="form-btn btn-blue"
                        onClick={handleExecute}
                        disabled={!selectedReport || executeLoading}
                    >
                        {executeLoading ? t('common.generating') : t('common.execute')}
                    </button>
                </div>
            </div>

            {selectedReport ? (
                <div className="flex-column gap-20 width-100">
                    {show(STAFF_MOVEMENT_SECTIONS.FOR_PERIOD) && (
                        <StaffMovementSection title={t('staff.staffMovement.period')}>
                            <CustomDatePicker
                                name="forPeriodDate"
                                value={forPeriodDate}
                                onChange={setForPeriodDate}
                                isSmall={true}
                                placeholderText={t('staff.staffMovement.period')}
                            />
                        </StaffMovementSection>
                    )}

                    {show(STAFF_MOVEMENT_SECTIONS.FROM_TO) && (
                        <StaffMovementSection>
                            {fromToDateFields}
                        </StaffMovementSection>
                    )}

                    {show(STAFF_MOVEMENT_SECTIONS.LANGUAGE_COMPANY) && (
                        <LanguageCompanySection
                            t={t}
                            language={language}
                            onLanguageChange={setLanguage}
                            companyId={companyId}
                            onCompanyChange={setCompanyId}
                            languageOptions={languageOptions}
                        />
                    )}

                    {show(STAFF_MOVEMENT_SECTIONS.FOR_PEOPLE) && (
                        <ForPeopleSection
                            t={t}
                            selectedReport={selectedReport}
                            peopleStatus={peopleStatus}
                            onPeopleStatusChange={setPeopleStatus}
                            peopleStatusOptions={peopleStatusOptions}
                        />
                    )}

                    {show(STAFF_MOVEMENT_SECTIONS.ADDITIONAL_COLUMNS) && (
                        <AdditionalColumnsSection
                            t={t}
                            extendedReport={extendedReport}
                            onExtendedReportChange={setExtendedReport}
                        />
                    )}
                </div>
            ) : null}
        </div>
    );
}
